/**
 * Order the timeline by execution, grouped under the task each step advanced.
 *
 * Plan position cannot order the list: positions are assigned before anything
 * runs, so a task that executed first would still render below one that has not
 * started. Groups therefore sort by when work on them was first observed, and
 * the untouched remainder of the plan follows in plan order — the only order
 * that means anything for work with no execution time yet.
 */

import { taskInRun } from './taskRuns'

function firstSeq(group) {
  const seqs = [group.task.first_progress_seq, ...group.rows.map((row) => row.startedSeq)]
    .filter((value) => typeof value === 'number')
  return seqs.length ? Math.min(...seqs) : null
}

function bySeq(a, b) {
  if (a.startedSeq == null) return b.startedSeq == null ? 0 : 1
  if (b.startedSeq == null) return -1
  return a.startedSeq - b.startedSeq
}

function byExecution(a, b) {
  const left = firstSeq(a)
  const right = firstSeq(b)
  if (left == null && right == null) return a.task.position - b.task.position
  if (left == null) return 1
  if (right == null) return -1
  return left - right
}

// `sequence` restarts every run while the checkpoint spans the conversation, so
// a message renders its own run's *rows* only. Rows with no run (live SSE,
// before the snapshot lands) belong to the run being streamed. Tasks are scoped
// differently — see `taskRuns` — because one task can span several runs while a
// row never does.
function inRun(runId, value) {
  return !runId || !value || value === runId
}

export function timelineGroups(workStatus, rows = [], runId = null) {
  const visible = rows.filter((row) => row.status !== 'superseded' && inRun(runId, row.originRunId))
  const byTask = new Map()
  visible.forEach((row) => {
    const key = row.taskId ?? null
    byTask.set(key, [...(byTask.get(key) ?? []), row])
  })
  const groups = (workStatus?.tasks ?? [])
    .filter((task) => taskInRun(runId, task))
    .map((task) => ({ task, rows: [...(byTask.get(task.id) ?? [])].sort(bySeq) }))
    .sort(byExecution)
  const known = new Set(groups.map((group) => group.task.id))
  const orphans = visible.filter((row) => !row.taskId || !known.has(row.taskId)).sort(bySeq)
  return { groups, orphans, trimmed: Boolean(workStatus?.activity_evicted_ids?.length) }
}
